/**
 * UI chrome dictionary.
 *
 * Static, ships with the code, and covers only *interface* text — navigation,
 * buttons, table headers, empty states. Editorial content (a news headline, a
 * member bio) is never here: that lives in the database as a `{zh, en}` pair
 * and is resolved with `pick()` from ./i18n.jsx.
 *
 * Keeping the two apart matters. UI strings are a developer's responsibility
 * and change with a deploy; content strings are an editor's responsibility and
 * change from the admin console. Mixing them means one of those two groups
 * eventually cannot change what they own.
 *
 * 界面文案词典。
 *
 * 静态、随代码发布，只覆盖「界面」文字——导航、按钮、表头、空状态。编辑内容
 * （新闻标题、成员简介）绝不出现在这里：那些以 {zh, en} 对存在数据库里，
 * 由 ./i18n.jsx 的 pick() 解析。
 *
 * 把两者分开很重要。界面文案归开发者，随发布变更；内容文案归编辑，
 * 在后台变更。混在一起，最终必有一方改不动本该属于自己的东西。
 */

/**
 * What a person sends to their AI to connect it to this site.
 *
 * This replaced a JSON configuration block, and the replacement is the point
 * rather than a rewording. The old block asked a reader to find their client's
 * config file, know what `args` means, and fill in the path to a checkout of a
 * private repository they would first have to be given. Every one of those steps
 * is a place where somebody who is not a programmer stops. None of them was
 * about what they actually wanted, which is for an AI to help write a news item.
 *
 * So the instructions are addressed to the AI, not to the person. The person
 * copies one block and sends it; the AI knows what an MCP server is and can
 * configure itself. `{url}` and `{key}` are filled in by the console — the URL
 * from the address the reader is actually using, so it is right whether they
 * came by domain or by IP, and the key from the one just minted.
 *
 * The last paragraph is not decoration. An AI that has read "drafts only" once,
 * in the words of the site it is about to write into, does not spend three turns
 * hunting for a publish call — and the closing instruction to list the
 * collections makes it prove the connection works, which is the question the old
 * page never answered.
 *
 * 一个人为了把 AI 接到本站，需要发出去的那段话。
 *
 * 它取代的是一段 JSON 配置，而「取代」本身就是要点，不是换个说法。旧的那段要求读者
 * 找到自己客户端的配置文件、知道 `args` 是什么意思，还要填上一个私有仓库工作副本的
 * 路径——而那份源码他得先被给到。上面每一步都是一个「不是程序员的人会卡住」的地方，
 * 而没有一步和他真正想做的事有关：他只是想让 AI 帮忙写条新闻。
 *
 * 所以这段话是写给 AI 的，不是写给人的。人只负责复制一整段发过去；AI 知道 MCP
 * 服务器是什么，能自己配好。`{url}` 与 `{key}` 由控制台填入——地址取自读者此刻
 * 真正在用的那个地址，所以不论他是用域名还是 IP 进来的都对；密钥取自刚签发的那把。
 *
 * 最后一段不是装饰。一个 AI 若已用「它即将写入的那个站点」的口吻读过一次「只能写草稿」，
 * 就不会再花三轮去找那个发布调用——而结尾那句「列出有哪些栏目」会让它当场证明连接是通的，
 * 这恰恰是旧页面从未回答过的问题。
 */
const AI_SETUP_PROMPT_ZH = `请把「Archaic Atom CVR 研究组」的网站接进你自己，这样你可以帮我起草内容。

它是一个 MCP 服务器，走 Streamable HTTP：

  地址：{url}/mcp
  认证：请求头 Authorization: Bearer {key}

配置请你自己完成，不需要我动手。弄好之后调用一次 aacvr_list_collections，
把这个站点有哪些栏目告诉我——这一步同时也是在确认确实接通了。

接进来之后，有两件事请一直记着：

一、你只能新建和修改草稿。发布必须由人在后台点一下，你没有这个能力，
    也不要试图绕过去。写完告诉我在哪儿看就行。

二、你从 PDF、截图或网页里转录的年份、经费数字、作者顺序，都可能出错。
    写完之后请把你拿不准的地方逐条列给我，不要默认它们是对的。`

const AI_SETUP_PROMPT_EN = `Please connect yourself to the Archaic Atom CVR research group's website so you can help me draft content.

It is an MCP server over Streamable HTTP:

  Address: {url}/mcp
  Auth:    header  Authorization: Bearer {key}

Set it up yourself — I should not have to do anything. Once it is connected,
call aacvr_list_collections once and tell me which sections this site has;
that also confirms the connection actually works.

Two things to keep in mind for as long as you are connected:

1. You can only create and edit drafts. Publishing is a person clicking a button
   in the console. You do not have that ability and should not try to work
   around it. Just tell me where to look when you are done.

2. Years, funding figures and author orders you transcribe from a PDF, a
   screenshot or a web page can come out wrong. When you finish, list the parts
   you are unsure about instead of assuming they are right.`

export const UI_STRINGS = {
  // -- Navigation / 导航 ---------------------------------------------------
  'nav.home': { zh: '首页', en: 'Home' },
  'nav.news': { zh: '新闻动态', en: 'News' },
  'nav.members': { zh: '研究成员', en: 'People' },
  'nav.research': { zh: '研究方向', en: 'Research' },
  'nav.publications': { zh: '发表论文', en: 'Publications' },
  'nav.achievements': { zh: '成果列表', en: 'Achievements' },
  'nav.contact': { zh: '联系我们', en: 'Contact' },
  'nav.admin': { zh: '后台', en: 'Console' },
  'nav.menu': { zh: '菜单', en: 'Menu' },
  'nav.skipToContent': { zh: '跳到主要内容', en: 'Skip to content' },

  // -- Common actions / 通用操作 -------------------------------------------
  'common.loading': { zh: '加载中…', en: 'Loading…' },
  'common.retry': { zh: '重试', en: 'Retry' },
  'common.save': { zh: '保存', en: 'Save' },
  'common.saving': { zh: '保存中…', en: 'Saving…' },
  'common.saved': { zh: '已保存', en: 'Saved' },
  'common.cancel': { zh: '取消', en: 'Cancel' },
  'common.delete': { zh: '删除', en: 'Delete' },
  'common.edit': { zh: '编辑', en: 'Edit' },
  'common.create': { zh: '新建', en: 'New' },
  'common.duplicate': { zh: '复制', en: 'Duplicate' },
  'common.close': { zh: '关闭', en: 'Close' },
  'common.confirm': { zh: '确定', en: 'Confirm' },
  'common.back': { zh: '返回', en: 'Back' },
  'common.search': { zh: '搜索', en: 'Search' },
  'common.filter': { zh: '筛选', en: 'Filter' },
  'common.clear': { zh: '清除', en: 'Clear' },
  'common.all': { zh: '全部', en: 'All' },
  'common.more': { zh: '更多', en: 'More' },
  'common.viewAll': { zh: '查看全部', en: 'View all' },
  'common.remove': { zh: '移除', en: 'Remove' },
  'common.upload': { zh: '上传', en: 'Upload' },
  'common.uploading': { zh: '上传中…', en: 'Uploading…' },
  'common.download': { zh: '下载', en: 'Download' },
  'common.optional': { zh: '选填', en: 'Optional' },
  'common.required': { zh: '必填', en: 'Required' },
  'common.moveUp': { zh: '上移', en: 'Move up' },
  'common.moveDown': { zh: '下移', en: 'Move down' },
  'common.count': { zh: '共 {n} 项', en: { one: '{n} item', other: '{n} items' } },
  'common.language': { zh: '语言', en: 'Language' },
  // Deliberately inverted: this is the label of the language you would switch
  // TO, so the Chinese entry reads 'English' and vice versa.
  // 刻意反着写：这是「切换过去的那种语言」的名字，所以中文条目写 English，反之亦然。
  'common.otherLanguage': { zh: 'English', en: '中文' },
  'common.theme': { zh: '主题', en: 'Theme' },
  'common.themeLight': { zh: '浅色', en: 'Light' },
  'common.themeDark': { zh: '深色', en: 'Dark' },
  'common.themeSystem': { zh: '跟随系统', en: 'System' },
  // Accessible name of the theme control. It names the CURRENT mode, because a
  // three-way cycle cannot promise what the next press will produce.
  // 主题控件的可访问名称。它报的是「当前」模式，因为三态循环无法承诺下一次按下会
  // 变成什么。
  'common.themeToggle': { zh: '主题：{mode}', en: 'Theme: {mode}' },

  // -- Status / 状态 -------------------------------------------------------
  'status.draft': { zh: '草稿', en: 'Draft' },
  'status.published': { zh: '已发布', en: 'Published' },
  'status.publish': { zh: '发布', en: 'Publish' },
  'status.unpublish': { zh: '转为草稿', en: 'Unpublish' },
  'status.pinned': { zh: '置顶', en: 'Pinned' },

  // -- Home / 首页 ---------------------------------------------------------
  'home.eyebrow': { zh: '计算机视觉与三维重建', en: 'Computer Vision & 3D Reconstruction' },
  'home.latestNews': { zh: '最新动态', en: 'Latest news' },
  'home.researchAreas': { zh: '研究方向', en: 'Research' },
  'home.selectedPublications': { zh: '代表性论文', en: 'Selected publications' },
  'home.joinUs': { zh: '加入我们', en: 'Join us' },
  'home.ctaResearch': { zh: '研究方向', en: 'Research' },
  'home.ctaPublications': { zh: '论文列表', en: 'Publications' },

  // -- News / 新闻 ---------------------------------------------------------
  'news.title': { zh: '新闻动态', en: 'News' },
  'news.lede': { zh: '课题组的活动、成果与公告。', en: 'Activities, results and announcements from the group.' },
  'news.empty': { zh: '还没有新闻', en: 'No news yet' },
  'news.emptyBody': {
    zh: '这个栏目已经就绪，等待第一条内容。登录后台即可发布。',
    en: 'The section is ready and waiting for its first entry. Sign in to the console to publish one.',
  },
  'news.readMore': { zh: '阅读全文', en: 'Read more' },
  'news.readingTime': { zh: '约 {n} 分钟', en: '{n} min read' },
  'news.notFound': { zh: '没有找到这条新闻', en: 'News item not found' },
  'news.backToList': { zh: '返回新闻列表', en: 'Back to news' },

  // -- Members / 成员 ------------------------------------------------------
  'members.title': { zh: '研究成员', en: 'People' },
  'members.lede': { zh: '课题组的教师与学生。', en: 'Faculty and students of the group.' },
  'members.faculty': { zh: '教师', en: 'Faculty' },
  'members.students': { zh: '学生', en: 'Students' },
  'members.alumni': { zh: '毕业去向', en: 'Alumni' },
  'members.phd': { zh: '博士研究生', en: 'PhD students' },
  'members.master': { zh: '硕士研究生', en: 'Master students' },
  'members.undergrad': { zh: '本科生', en: 'Undergraduates' },
  'members.empty': { zh: '还没有成员信息', en: 'No members listed yet' },
  'members.emptyBody': {
    zh: '教师与学生的名单还没有录入。登录后台即可添加第一位成员。',
    en: 'Faculty and students have not been entered yet. Sign in to the console to add the first one.',
  },
  'members.interests': { zh: '研究兴趣', en: 'Research interests' },
  'members.destination': { zh: '去向', en: 'Now at' },
  // Shown under an alumnus's photo. A bare year in a caption reads as ambiguous —
  // enrolled? graduated? — and the cohort line above it on a student card sets the
  // expectation that this slot says something about when.
  // 显示在校友照片下方。标题位上光秃秃一个年份是有歧义的——入学？毕业？——
  // 而学生卡片上同一位置的年级行，已经让人预期这里说的是「什么时候」。
  'members.graduatedIn': { zh: '{year} 届毕业', en: 'Class of {year}' },
  'members.homepage': { zh: '个人主页', en: 'Homepage' },
  'members.email': { zh: '邮箱', en: 'Email' },
  // Proper nouns, but still routed through the dictionary so no component ever
  // hardcodes a user-visible string.
  // 虽是专有名词，仍走词典，保证没有任何组件写死可见文案。
  'members.github': { zh: 'GitHub', en: 'GitHub' },
  'members.scholar': { zh: 'Google Scholar', en: 'Google Scholar' },

  // -- Research / 研究方向 -------------------------------------------------
  'research.title': { zh: '研究项目', en: 'Research Projects' },
  'research.lede': {
    zh: '我们主持的科研项目，以及做出来的东西。',
    en: 'The projects we lead, and what they actually produce.',
  },
  'research.projects': { zh: '主持项目', en: 'Funded projects' },
  'research.empty': { zh: '还没有研究项目', en: 'No research projects yet' },
  'research.emptyBody': {
    zh: '课题组的研究项目还没有录入。登录后台即可添加第一个。',
    en: 'Research projects have not been entered yet. Sign in to the console to add the first one.',
  },
  'research.pi': { zh: '主持人', en: 'Principal investigator' },
  'research.funder': { zh: '资助来源', en: 'Funder' },
  'research.grantNo': { zh: '项目编号', en: 'Grant no.' },
  'research.period': { zh: '起止时间', en: 'Period' },
  'research.amount': { zh: '经费', en: 'Funding' },
  'research.role': { zh: '承担角色', en: 'Role' },
  'research.status': { zh: '进展', en: 'Progress' },
  'research.rolePi': { zh: '主持', en: 'Principal investigator' },
  'research.roleCopi': { zh: '参与', en: 'Co-investigator' },
  'research.ongoing': { zh: '在研', en: 'Ongoing' },
  'research.finished': { zh: '已结题', en: 'Completed' },
  'research.notFound': { zh: '没有找到该项目', en: 'Project not found' },
  'research.backToList': { zh: '返回研究项目', en: 'Back to projects' },

  // -- Publications / 论文 -------------------------------------------------
  'publications.title': { zh: '发表论文', en: 'Publications' },
  'publications.lede': { zh: '期刊与会议论文，按年份排列。', en: 'Journal and conference papers, newest first.' },
  'publications.empty': { zh: '还没有论文', en: 'No publications yet' },
  'publications.emptyBody': {
    zh: '论文列表还是空的。登录后台即可录入第一篇。',
    en: 'The publication list is still empty. Sign in to the console to add the first paper.',
  },
  'publications.filters': { zh: '筛选条件', en: 'Filters' },
  'publications.filterYear': { zh: '年份', en: 'Year' },
  'publications.filterVenue': { zh: '发表载体', en: 'Venue' },
  'publications.filterType': { zh: '类型', en: 'Type' },
  'publications.typeJournal': { zh: '期刊', en: 'Journal' },
  'publications.typeConference': { zh: '会议', en: 'Conference' },
  'publications.typePreprint': { zh: '预印本', en: 'Preprint' },
  'publications.paper': { zh: '原文', en: 'Paper' },
  'publications.pdf': { zh: 'PDF', en: 'PDF' },
  'publications.code': { zh: '代码', en: 'Code' },
  'publications.doi': { zh: 'DOI', en: 'DOI' },
  'publications.bibtex': { zh: 'BibTeX', en: 'BibTeX' },
  'publications.copyBibtex': { zh: '复制 BibTeX', en: 'Copy BibTeX' },
  'publications.copied': { zh: '已复制', en: 'Copied' },
  'publications.citations': { zh: '被引 {n}', en: '{n} citations' },
  'publications.showing': { zh: '显示 {n} / {total} 篇', en: 'Showing {n} of {total}' },
  'publications.showMore': { zh: '展开其余 {n} 项', en: '{n} more' },
  'publications.showLess': { zh: '收起', en: 'Show less' },
  'publications.noMatch': { zh: '没有符合筛选条件的论文', en: 'No publications match these filters' },
  'publications.clearFilters': { zh: '清除全部筛选', en: 'Clear all filters' },

  // -- Achievements / 成果 -------------------------------------------------
  'achievements.title': { zh: '成果列表', en: 'Achievements' },
  'achievements.lede': {
    zh: '专利、软件著作权与竞赛获奖。',
    en: 'Patents, software copyrights and competition awards.',
  },
  'achievements.patents': { zh: '专利软著', en: 'Patents & software' },
  'achievements.awards': { zh: '竞赛获奖', en: 'Awards' },
  'achievements.empty': { zh: '还没有成果记录', en: 'No achievements listed yet' },
  'achievements.emptyBody': {
    zh: '专利、软著与获奖都还没有录入。登录后台即可添加第一条。',
    en: 'No patents, software copyrights or awards have been entered yet. Sign in to the console to add the first one.',
  },
  'achievements.kindInvention': { zh: '发明专利', en: 'Invention patent' },
  'achievements.kindUtility': { zh: '实用新型', en: 'Utility model' },
  'achievements.kindSoftware': { zh: '软件著作权', en: 'Software copyright' },
  'achievements.number': { zh: '专利号', en: 'Number' },
  'achievements.granted': { zh: '已授权', en: 'Granted' },
  'achievements.pending': { zh: '实审中', en: 'Pending' },
  'achievements.levelInternational': { zh: '国际级', en: 'International' },
  'achievements.levelNational': { zh: '国家级', en: 'National' },
  'achievements.levelProvincial': { zh: '省部级', en: 'Provincial' },
  'achievements.levelSchool': { zh: '校级', en: 'University' },
  'achievements.recipients': { zh: '获奖人', en: 'Recipients' },
  'achievements.advisor': { zh: '指导教师', en: 'Advisor' },
  'achievements.inventors': { zh: '发明人 / 著作权人', en: 'Inventors' },
  'achievements.certificate': { zh: '证书', en: 'Certificate' },
  'achievements.viewCertificate': { zh: '查看证书', en: 'View certificate' },
  'achievements.downloadOriginal': { zh: '下载原件', en: 'Download original' },

  // -- Research overview / 研究方向总览 --------------------------------------
  'research.overviewTitle': { zh: '研究方向', en: 'Research' },
  'research.overviewLede': {
    zh: '我们主持的科研项目，以及这些工作做出来的演示样例。',
    en: 'The projects we lead, and the demos our work produces.',
  },
  'research.viewAllProjects': { zh: '查看全部研究项目', en: 'All research projects' },

  // -- Demos / 演示样例 ------------------------------------------------------
  'demos.title': { zh: '演示样例', en: 'Demos' },
  'demos.lede': {
    zh: '研究成果与项目工程的演示：程序、运行结果、视频。',
    en: 'Demonstrations of research results and engineering work: programs, results, recordings.',
  },
  'demos.empty': { zh: '还没有演示样例', en: 'No demos yet' },
  'demos.emptyBody': {
    zh: '这个栏目已经就绪，等待第一个演示。登录后台即可添加。',
    en: 'The section is ready and waiting for its first demo. Sign in to the console to add one.',
  },
  'demos.viewAll': { zh: '查看全部演示样例', en: 'All demos' },
  'demos.kind': { zh: '类别', en: 'Category' },
  'demos.kindResult': { zh: '研究成果', en: 'Research result' },
  'demos.kindEngineering': { zh: '项目工程', en: 'Engineering' },
  'demos.year': { zh: '时间', en: 'Year' },
  'demos.video': { zh: '视频', en: 'Video' },
  'demos.relatedProject': { zh: '相关项目', en: 'Related project' },
  'demos.notFound': { zh: '没有找到该演示样例', en: 'Demo not found' },
  'demos.backToList': { zh: '返回演示样例', en: 'Back to demos' },

  // -- Contact / 联系方式 --------------------------------------------------
  'contact.title': { zh: '联系我们', en: 'Contact' },
  'contact.lede': { zh: '欢迎交流与合作。', en: 'We welcome collaboration and enquiries.' },
  'contact.address': { zh: '地址', en: 'Address' },
  'contact.email': { zh: '邮箱', en: 'Email' },
  'contact.phone': { zh: '电话', en: 'Phone' },
  'contact.institution': { zh: '所属单位', en: 'Institution' },
  'contact.department': { zh: '院系 / 实验室', en: 'Department' },
  'contact.people': { zh: '联系人', en: 'Who to contact' },
  'contact.peopleEmpty': {
    zh: '还没有联系人。加一位，访客就知道该找谁，而不是只对着公共邮箱。',
    en: 'No contacts yet. Add one so visitors know who to write to, not just where.',
  },
  'contact.addPerson': { zh: '添加联系人', en: 'Add contact' },
  'contact.personName': { zh: '姓名', en: 'Name' },
  'contact.personRole': { zh: '负责事项', en: 'Enquiries about' },
  'contact.forStudents': { zh: '致有意报考的同学', en: 'For prospective students' },
  'contact.wechat': { zh: '微信', en: 'WeChat' },

  // -- Admin: auth / 后台：登录 --------------------------------------------
  'admin.signIn': { zh: '登录', en: 'Sign in' },
  'admin.signOut': { zh: '退出登录', en: 'Sign out' },
  'admin.starterPasswordTitle': {
    zh: '这个账号还在用初始密码',
    en: 'This account still uses its starter password',
  },
  'admin.starterPasswordBody': {
    zh: '初始密码写在安装说明里，谁都查得到，而站点在公网上。请改成只有你知道的密码。',
    en: 'The starter password is printed in the install notes, so anyone can look it up — and this site is on the public internet. Change it to one only you know.',
  },
  'admin.starterPasswordAction': { zh: '立即修改', en: 'Change it now' },
  'admin.narrowTitle': {
    zh: '这一页需要更宽的屏幕',
    en: 'This page needs a wider screen',
  },
  'admin.narrowBody': {
    // Says what to do, not just what is wrong. "Rotate" is listed first because
    // it is the one that works without leaving the phone.
    // 说清该怎么办，而不只是说哪里不对。「横过来」放在最前面，
    // 因为它是唯一不用换设备就能解决的办法。
    zh: '编辑器、账号管理和站点设置在手机宽度下会挤成一团，容易改错。把手机横过来、把窗口拉宽，或者换电脑打开都可以。浏览和统计不受影响。',
    en: 'The editor, account management and site settings collapse at phone width, which makes them easy to get wrong. Rotate the phone, widen the window, or open it on a computer. Browsing and statistics are unaffected.',
  },
  'admin.narrowAction': { zh: '返回概览', en: 'Back to overview' },
  'admin.username': { zh: '用户名', en: 'Username' },
  'admin.password': { zh: '密码', en: 'Password' },
  'admin.loginTitle': { zh: '内容管理后台', en: 'Content console' },
  'admin.loginLede': { zh: '请使用管理员或编辑账号登录。', en: 'Sign in with an admin or editor account.' },
  'admin.loginFailed': { zh: '用户名或密码不正确', en: 'Incorrect username or password' },
  'admin.backToSite': { zh: '返回网站', en: 'Back to site' },

  // The console reached over plain HTTP. Said before the password is typed,
  // because the server refuses the request either way, and on the form that
  // refusal is indistinguishable from a wrong password unless it explains itself.
  // 经明文 HTTP 打开的后台。要赶在输入密码之前把话说清楚——服务端两种情况都会拒绝，
  // 而除非表单自己解释，这次拒绝在界面上和「密码错了」长得一模一样。
  'admin.insecureTitle': { zh: '当前连接未加密', en: 'This connection is not encrypted' },
  'admin.insecureBody': {
    zh: '后台登录必须走 HTTPS，否则密码会以明文经过网络。你现在访问的是 {origin}，所以登录会被拒绝——这不是密码错误。',
    en:
      'Signing in requires HTTPS, or your password crosses the network in the clear. ' +
      'You are on {origin}, so sign-in is refused — this is not a wrong password.',
  },
  // Shown only when the encrypted address is a bare IP, which is the situation
  // until ICP filing clears: the certificate is issued to the domain, so the
  // browser objects to the *name* while the connection itself is encrypted
  // exactly as it should be. Said in advance, because an unexplained red
  // interstitial reads as "this site is broken" and is the one screen a person
  // is most likely to back out of.
  // 仅当加密地址是裸 IP 时显示，也就是备案下来之前的状态：证书是签给域名的，
  // 因此浏览器抗议的是「名字」，而连接本身该怎么加密就怎么加密。要提前说，
  // 因为一个没人解释过的红色拦截页读起来就是「这网站坏了」，
  // 而那恰恰是人最可能掉头就走的一屏。
  'admin.insecureCertNote': {
    zh: '浏览器会提示证书名称不符——因为证书签给的是域名，而域名在备案完成前被拦截。连接本身是加密的，选择「高级 / 继续前往」即可。',
    en:
      'The browser will warn that the certificate name does not match: it was issued for the domain, ' +
      'which is blocked until ICP filing completes. The connection itself is encrypted — choose ' +
      '"Advanced / Proceed" to continue.',
  },
  'admin.insecureGo': { zh: '切换到 HTTPS 并继续', en: 'Switch to HTTPS and continue' },
  'admin.insecureStay': { zh: '留在此页', en: 'Stay here' },
  'admin.insecureStayed': {
    zh: '已留在未加密连接上。这里无法登录后台；公开页面照常浏览。',
    en: 'Staying on the unencrypted connection. Sign-in is not possible here; public pages work as usual.',
  },

  // -- Admin: shell / 后台：框架 -------------------------------------------
  'admin.dashboard': { zh: '后台概览', en: 'Overview' },
  'admin.content': { zh: '内容', en: 'Content' },
  'admin.system': { zh: '系统', en: 'System' },
  'admin.media': { zh: '媒体资源', en: 'Media' },
  'admin.users': { zh: '账号管理', en: 'Accounts' },
  'admin.settings': { zh: '站点设置', en: 'Site settings' },
  'admin.appearance': { zh: '外观设置', en: 'Appearance' },
  'admin.appearanceLede': {
    zh: '调整全站文字大小。改动对所有访客生效，仅管理员可改。',
    en: 'Adjust the site-wide text size. It applies to every visitor, and only admins can change it.',
  },
  'admin.account': { zh: '我的账号', en: 'My account' },
  'admin.accountLede': {
    zh: '修改自己的密码，以及只影响这台浏览器的界面偏好。',
    en: 'Change your own password, and set interface preferences that stay in this browser.',
  },
  'admin.preferences': { zh: '界面偏好', en: 'Preferences' },
  'admin.themePack': { zh: '主题风格', en: 'Theme' },
  'admin.signedInAs': { zh: '当前账号', en: 'Signed in as' },
  'admin.you': { zh: '本人', en: 'You' },
  'admin.openMenu': { zh: '展开导航', en: 'Open navigation' },
  'admin.closeMenu': { zh: '收起导航', en: 'Close navigation' },
  'admin.roleAdmin': { zh: '管理员', en: 'Admin' },
  'admin.roleEditor': { zh: '编辑', en: 'Editor' },
  // Kept although no account can be given this role any more: `agent` is what an
  // API key acts as, and the accounts page still has to *name* an account left
  // over from before keys existed. Drop the string and that row would be drawn
  // wearing somebody else's role. The raw word stays inside the label because it
  // is the word the permission table, the server's refusals and the MCP guide
  // all use for the same thing.
  // 保留它，尽管已经没有账号能被赋予这个角色：agent 是 API 密钥行事时的角色，
  // 而账号页仍然需要「叫得出」密钥出现之前遗留下来的账号是什么。删掉这条文案，
  // 那一行就会被画成别人的角色。标签里保留原词，是因为权限表、服务端的拒绝消息
  // 和 MCP 指南说的都是这同一个词。
  'admin.roleAgent': { zh: 'AI 助手（agent）', en: 'AI assistant (agent)' },
  'admin.changePassword': { zh: '修改密码', en: 'Change password' },
  'admin.currentPassword': { zh: '当前密码', en: 'Current password' },
  'admin.newPassword': { zh: '新密码', en: 'New password' },
  'admin.confirmPassword': { zh: '确认新密码', en: 'Confirm new password' },
  'admin.passwordChanged': { zh: '密码已修改', en: 'Password changed' },
  'admin.passwordMismatch': { zh: '两次输入的新密码不一致', en: 'The two new passwords do not match' },
  'admin.passwordTooShort': { zh: '密码至少 {n} 位', en: 'Password needs at least {n} characters' },
  'admin.adminOnly': { zh: '仅管理员可访问', en: 'Admins only' },
  'admin.adminOnlyBody': {
    zh: '这个页面需要管理员权限。如需变更，请联系管理员。',
    en: 'This page requires an admin account. Ask an administrator if you need access.',
  },

  // -- Admin: dashboard / 后台：概览 ---------------------------------------
  'admin.welcome': { zh: '你好，{name}', en: 'Hello, {name}' },
  'admin.dashboardLede': {
    zh: '各栏目现有多少内容，以及最近改动了什么。',
    en: 'How much content each section holds, and what changed most recently.',
  },
  'admin.recentlyEdited': { zh: '最近编辑', en: 'Recently edited' },
  'admin.recentEmpty': { zh: '还没有任何编辑记录', en: 'Nothing has been edited yet' },
  'admin.quickCreate': { zh: '快速新建', en: 'Quick create' },
  'admin.draftCount': { zh: '{n} 篇草稿', en: '{n} drafts' },

  // -- Admin: lists & forms / 后台：列表与表单 ------------------------------
  'admin.title': { zh: '标题', en: 'Title' },
  'admin.collection': { zh: '栏目', en: 'Section' },
  'admin.status': { zh: '状态', en: 'Status' },
  'admin.updated': { zh: '更新时间', en: 'Updated' },
  'admin.actions': { zh: '操作', en: 'Actions' },
  'admin.searchPlaceholder': { zh: '搜索标题、作者、标签…', en: 'Search titles, authors, tags…' },
  'admin.noRecords': { zh: '这个栏目还没有内容', en: 'Nothing in this section yet' },
  'admin.noRecordsBody': { zh: '新建第一条「{name}」记录。', en: 'Create the first {name} entry.' },
  'admin.noMatches': { zh: '没有匹配的记录', en: 'No records match' },
  'admin.viewOnSite': { zh: '在网站上查看', en: 'View on site' },
  'admin.newRecord': { zh: '新建{name}', en: 'New {name}' },
  'admin.editRecord': { zh: '编辑{name}', en: 'Edit {name}' },
  'admin.deleteRecord': { zh: '删除这条记录？', en: 'Delete this record?' },
  'admin.deleteRecordBody': {
    zh: '删除后无法恢复。已上传的图片和文件不会被删除。',
    en: 'This cannot be undone. Uploaded images and files are kept.',
  },
  'admin.deleted': { zh: '已删除', en: 'Deleted' },
  'admin.unsavedTitle': { zh: '有未保存的修改', en: 'Unsaved changes' },
  'admin.unsavedBody': { zh: '离开这个页面会丢失修改。', en: 'Leaving this page will discard them.' },
  'admin.leave': { zh: '离开', en: 'Leave' },
  'admin.stay': { zh: '留下', en: 'Stay' },
  'admin.fieldZh': { zh: '中文', en: 'Chinese' },
  'admin.fieldEn': { zh: 'English', en: 'English' },

  // -- Admin: users / 后台：账号 -------------------------------------------
  'admin.usersLede': {
    zh: '管理员可以新建、停用编辑账号并重置密码。',
    en: 'Admins can create and deactivate editor accounts, and reset passwords.',
  },
  'admin.displayName': { zh: '显示名称', en: 'Display name' },
  'admin.role': { zh: '角色', en: 'Role' },
  'admin.active': { zh: '启用', en: 'Active' },
  'admin.inactive': { zh: '已停用', en: 'Disabled' },
  'admin.lastLogin': { zh: '最近登录', en: 'Last sign-in' },
  'admin.never': { zh: '从未', en: 'Never' },
  'admin.createUser': { zh: '新建账号', en: 'New account' },
  'admin.userCreated': { zh: '账号已创建', en: 'Account created' },
  'admin.userUpdated': { zh: '账号已更新', en: 'Account updated' },
  'admin.cannotDeleteSelf': { zh: '不能删除自己的账号', en: 'You cannot delete your own account' },
  'admin.resetPassword': { zh: '重置密码', en: 'Reset password' },
  'admin.deleteUser': { zh: '删除账号？', en: 'Delete account?' },
  'admin.deleteUserBody': {
    zh: '该账号将无法再登录。TA 已发布的内容会保留。',
    en: 'The account will no longer be able to sign in. Content they published is kept.',
  },
  'admin.lastAdminWarning': {
    zh: '不能删除或降级最后一个管理员账号。',
    en: 'The last admin account cannot be removed or demoted.',
  },

  // -- Admin: media / 后台：媒体库 -----------------------------------------
  'admin.mediaLede': { zh: '所有上传的图片、视频、音频与文档。', en: 'Every uploaded image, video, audio file and document.' },
  'admin.mediaEmpty': { zh: '媒体库是空的', en: 'The media library is empty' },
  'admin.copyUrl': { zh: '复制链接', en: 'Copy URL' },
  'admin.copied': { zh: '链接已复制', en: 'URL copied' },
  'admin.copyFailed': { zh: '复制失败，请手动选择链接', en: 'Copy failed — select the URL manually' },
  'admin.deleteAsset': { zh: '删除这个文件？', en: 'Delete this file?' },
  'admin.deleteAssetBody': {
    zh: '引用了这个文件的页面会出现空白。请先确认没有页面在用。',
    en: 'Pages referencing it will show a gap. Check nothing uses it first.',
  },

  // -- Admin: site settings / 后台：站点设置 --------------------------------
  'admin.settingsLede': {
    zh: '课题组名称、联系方式与首页素材。保存后立刻反映到公开页。',
    en: 'Group name, contact details and home-page assets. Saved changes reach the public site immediately.',
  },

  // -- Admin: AI assistant / 后台：AI 助手 -----------------------------------
  'mcp.title': { zh: 'AI 助手', en: 'AI assistant' },
  'mcp.lede': {
    zh: '把 AI 接到这个站点，让它替你起草内容。发布仍然由人来做。',
    en: 'Point an AI at this site and let it draft content for you. Publishing stays with a human.',
  },
  'mcp.introTitle': { zh: '这是什么', en: 'What this is' },
  'mcp.introBody': {
    zh: 'AI 可以在这里起草内容，但它不能发布、不能改动任何已经发布的内容，也不能删除任何东西。',
    en: 'An AI can draft content here; it cannot publish, cannot edit anything already published, and cannot delete anything.',
  },
  'mcp.introReview': {
    zh: '草稿由人来审阅，确认无误后由人点击发布。',
    en: 'A human reviews the draft and publishes it.',
  },
  'mcp.limitPublish': { zh: '不能发布', en: 'Cannot publish' },
  'mcp.limitEditPublished': { zh: '不能改动已发布的内容', en: 'Cannot edit published records' },
  'mcp.limitDelete': { zh: '不能删除任何东西', en: 'Cannot delete anything' },

  // -- Admin: API keys / 后台：API 密钥 --------------------------------------
  // Kept apart from the guide's prose above because it is a different kind of
  // text: table headers and button labels, said at the moment a person is
  // actually holding a credential in their hands rather than reading about one.
  // 与上面的指南叙述分开，因为这是另一类文字：表头与按钮文案，
  // 说在「一个人手里真的握着一份凭据」的那一刻，而不是「读到有这么回事」的时候。
  'keys.myTitle': { zh: '我的密钥', en: 'My keys' },
  'keys.myLede': {
    zh: '密钥由你自己签发，不需要管理员经手。AI 用你的密钥写下的内容会记在你名下。',
    en: 'You mint your own keys; no admin is involved. Whatever an AI writes with yours is recorded as written by you.',
  },
  // The rule that makes an admin's own key safe to put in a config file, said
  // exactly where an admin is about to mint one. Attribution follows the owner;
  // capability never does. If a reader takes away only one sentence from this
  // page, this is the one that has to be it.
  // 让「管理员自己的密钥也能安心写进配置文件」的那条规则，就写在管理员即将签发它的地方。
  // 署名跟着所属人走，权限永远不跟。如果读者只从这一页带走一句话，那必须是这一句。
  'keys.rule': {
    zh: '密钥永远只有 agent 权限——哪怕签发它的是管理员本人。它写出来的一律是草稿，发布依旧由人来点。',
    en: 'A key always carries agent permissions — even one an admin minted for themselves. Everything it writes is a draft, and publishing still takes a person.',
  },
  'keys.allTitle': { zh: '全部密钥', en: 'All keys' },
  'keys.allLede': {
    zh: '站点上现存的每一把密钥，包括别人的。管理员可以吊销其中任意一把。',
    en: 'Every key that exists on this site, other people’s included. An admin may revoke any of them.',
  },
  'keys.create': { zh: '新建密钥', en: 'Create key' },
  // Numbered to pair with 'mcp.configTitle'. Two steps is the whole setup, and
  // saying so up front is what tells a reader this is short.
  // 编号与 'mcp.configTitle' 配对。整个接入就两步，把这一点先说出来，
  // 读者才知道这件事很短。
  'keys.createTitle': { zh: '第一步：申请一把密钥', en: 'Step one: mint a key' },
  'keys.name': { zh: '名称', en: 'Name' },
  'keys.nameHint': {
    zh: '写清楚它会放在哪台机器上，例如「办公室台式机」。以后要吊销时，你就靠这个名字认出它。',
    en: 'Say which machine it will live on, e.g. “office desktop”. This name is how you will recognise it later, when you revoke it.',
  },
  'keys.nameRequired': { zh: '请先给这把密钥起个名字', en: 'Give the key a name first' },
  'keys.noneActive': { zh: '当前没有在用的密钥', en: 'No keys currently in use' },
  'keys.noneActiveBody': {
    zh: '你签发过的都已吊销，记录在下面。要再接一个 AI，签一把新的即可。',
    en: 'Everything you minted has been revoked; the record is below. To connect an AI again, mint a new one.',
  },
  'keys.historyTitle': {
    zh: '已吊销 · {n} 把',
    en: { one: 'Revoked · {n} key', other: 'Revoked · {n} keys' },
  },
  'keys.historyNote': {
    zh: '吊销记录不删除：它回答的是「某段时间里谁有过访问权」，删掉就没人答得上来了。',
    en: 'Revocations are kept rather than deleted: they are what answers “who had access, and when” — delete them and nothing can.',
  },
  'keys.prefix': { zh: '前缀', en: 'Prefix' },
  'keys.createdAt': { zh: '创建于', en: 'Created' },
  'keys.lastUsed': { zh: '最近使用', en: 'Last used' },
  'keys.neverUsed': { zh: '从未使用', en: 'Never used' },
  'keys.owner': { zh: '所属人', en: 'Owner' },
  'keys.revoke': { zh: '吊销', en: 'Revoke' },
  'keys.revoked': { zh: '密钥已吊销', en: 'Key revoked' },
  // The table's word for a row that is dead but still listed — the site keeps
  // revoked keys so a leak can be traced afterwards. Kept apart from
  // `keys.revoked`, which is what a toast says once, in the moment: a column
  // that repeated a sentence written for that moment would read as if every
  // dead key had just been killed.
  // 表格里给「已经死掉但仍在列表中」的那一行用的词——站点保留已吊销的密钥，
  // 是为了事后能追查泄漏。与 `keys.revoked` 分开：那句是提示条在那一刻说一次的话，
  // 若让某一列重复这句为「那一刻」写的话，读起来就像每把死掉的密钥都是刚刚被吊销的。
  'keys.revokedTag': { zh: '已吊销', en: 'Revoked' },
  'keys.revokeTitle': { zh: '吊销这把密钥？', en: 'Revoke this key?' },
  'keys.revokeBody': {
    zh: '还在用它的 AI 客户端会立刻失效，得在配置里换一把新的。它此前写下的草稿会保留。',
    en: 'Any AI client still using it stops working immediately and needs a new key in its configuration. Drafts it already wrote are kept.',
  },
  'keys.empty': { zh: '还没有密钥', en: 'No keys yet' },
  'keys.emptyBody': {
    zh: '要把 AI 客户端接到这个站点，先在这里签一把密钥。',
    en: 'Mint one here to connect an AI client to this site.',
  },
  'keys.allEmpty': { zh: '站点上还没有人签发过密钥', en: 'Nobody has minted a key yet' },
  'keys.loadFailed': { zh: '密钥列表没能加载：{message}', en: 'The key list did not load: {message}' },

  // The one moment the secret exists on screen. The warning is phrased as a
  // consequence rather than a reminder — "you will not see it again" instead of
  // "please copy it now" — because the reader has to understand that leaving
  // this panel destroys something, not that they are being nagged to hurry.
  // 秘密在屏幕上存在的唯一一瞬。警告写成「后果」而不是「提醒」——
  // 说「你不会再看到它」，而不是说「请及时复制」——
  // 因为读者需要明白的是「离开这块面板就毁掉了一样东西」，而不是「有人在催我快点」。
  'keys.revealTitle': { zh: '这就是密钥本身，只显示这一次', en: 'This is the key itself — shown this once' },
  'keys.revealWarning': {
    zh: '离开这一页，它就再也不会出现了。站点只保存它的摘要，没有任何办法把它找回来；弄丢了只能重新签一把。',
    en: 'Leave this page and it is gone for good. The site keeps only a digest of it and has no way to show it again; lose it and you mint another.',
  },
  'keys.revealHint': {
    zh: '把它填进下面配置里的 AACVR_API_KEY。',
    en: 'Paste it into AACVR_API_KEY in the configuration below.',
  },
  'keys.copyKey': { zh: '复制密钥', en: 'Copy key' },
  'keys.keyCopied': { zh: '密钥已复制', en: 'Key copied' },
  'keys.revealDone': { zh: '我已经复制好了', en: 'I have copied it' },

  'mcp.configTitle': { zh: '第二步：把这段话发给你的 AI', en: 'Step two: send this to your AI' },
  'mcp.configLede': {
    zh: '不用装任何东西，也不用找配置文件在哪儿。复制下面整段，发给你的 AI 助手，让它自己接上——最后一句会让它当场验证有没有接通。',
    en: 'Nothing to install, and no configuration file to hunt for. Copy the whole block below and send it to your AI assistant; it connects itself. The last line makes it prove the connection worked.',
  },
  'mcp.configSample': { zh: AI_SETUP_PROMPT_ZH, en: AI_SETUP_PROMPT_EN },
  'mcp.copyConfig': { zh: '复制这段话', en: 'Copy this message' },
  'mcp.configCopied': { zh: '已复制，发给你的 AI 就行', en: 'Copied — send it to your AI' },
  'mcp.configNoKey': {
    zh: '上面这段里的密钥位置还是空的。先在「第一步」申请一把密钥，这段话会自动填好。',
    en: 'The key is still a placeholder above. Mint one in step one and this message fills itself in.',
  },
  'mcp.keyInChat': {
    zh: '这段话里带着你的密钥，所以它会进入你和 AI 的聊天记录。这正是密钥一人一把的原因：它只能写草稿、发布不了，而且你随时可以在上面的列表里把它吊销。别把它贴到群里或公开的地方。',
    en: 'This message carries your key, so it will end up in your chat history with the AI. That is exactly why keys are per person: it can only write drafts, it cannot publish, and you can revoke it in the list above at any time. Do not paste it into a group chat or anywhere public.',
  },
  'mcp.insecureOrigin': {
    zh: '你正在用 http 访问本站，所以上面这段里的地址也是 http——密钥会以明文经过网络。站点的域名和证书启用后，用域名打开本页，这段话里的地址会自动变成 https。',
    en: 'You are viewing this site over plain http, so the address above is http too — the key would cross the network in the clear. Once the domain and its certificate are live, open this page by domain and the address here becomes https on its own.',
  },
  'mcp.copyFailed': {
    zh: '复制失败，请手动选中上面的文本',
    en: 'Copy failed — select the text above manually',
  },

  'mcp.toolsTitle': { zh: 'AI 能做什么', en: 'What the AI can do' },
  'mcp.toolsLede': {
    zh: '接上之后，AI 手里只有这六个工具。没有发布工具，也没有删除工具——它们的缺席就是这套设计本身。',
    en: 'Once connected, the AI holds exactly these six tools. There is no publish tool and no delete tool; their absence is the design.',
  },
  // Tool names are identifiers, not prose, so both languages read the same. They
  // still go through the dictionary — the rule is that no component hardcodes a
  // user-visible string, and an identifier on screen is one.
  // 工具名是标识符而非文案，两种语言写法相同。它们依然走词典——
  // 规则是「组件不写死任何可见文案」，屏幕上的标识符也算。
  'mcp.toolListCollectionsName': { zh: 'aacvr_list_collections', en: 'aacvr_list_collections' },
  'mcp.toolListRecordsName': { zh: 'aacvr_list_records', en: 'aacvr_list_records' },
  'mcp.toolGetRecordName': { zh: 'aacvr_get_record', en: 'aacvr_get_record' },
  'mcp.toolCreateDraftName': { zh: 'aacvr_create_draft', en: 'aacvr_create_draft' },
  'mcp.toolUpdateDraftName': { zh: 'aacvr_update_draft', en: 'aacvr_update_draft' },
  'mcp.toolUploadAssetName': { zh: 'aacvr_upload_asset', en: 'aacvr_upload_asset' },
  'mcp.toolListCollections': {
    zh: '列出可以写入的栏目，以及每个栏目有哪些字段。',
    en: 'Lists the sections that can be written to, and the fields each one has.',
  },
  'mcp.toolListRecords': {
    zh: '列出某个栏目里的记录，可以按状态筛选。',
    en: 'Lists the records in a section, filterable by status.',
  },
  'mcp.toolGetRecord': { zh: '读取一条记录的完整内容。', en: 'Reads one record in full.' },
  'mcp.toolCreateDraft': {
    zh: '新建一条记录，状态永远是草稿。',
    en: 'Creates a record — always with status draft.',
  },
  'mcp.toolUpdateDraft': {
    zh: '修改一条草稿；已发布的记录它改不动。',
    en: 'Updates a draft; a record that is already published it cannot touch.',
  },
  'mcp.toolUploadAsset': {
    zh: '把本机上的图片或文档上传到媒体库。',
    en: 'Uploads an image or a document from a local path into the media library.',
  },

  'mcp.reviewTitle': { zh: '审阅与发布', en: 'Review and publish' },
  'mcp.reviewStep1': {
    zh: 'AI 写好的记录出现在对应栏目的列表里，状态标为「{draft}」。',
    en: 'The AI’s records appear in that section’s list, marked {draft}.',
  },
  'mcp.reviewStep2': {
    zh: '点开它，对着原始资料逐项核对。',
    en: 'Open it and check it against the source, item by item.',
  },
  'mcp.reviewStep3': {
    zh: '确认无误后点「{publish}」，内容这才出现在公开页上。',
    en: 'When it is right, click {publish} — only then does it reach the public site.',
  },

  'mcp.cautionTitle': { zh: '审阅不是走过场', en: 'The review is not a formality' },
  'mcp.cautionBody': {
    zh: 'AI 从 PDF 里摘内容时，偶尔会把某个数字或作者顺序弄错，而这种错误读起来完全像对的。发布之前，请对着原文核一遍。',
    en: 'An AI drafting from a PDF will sometimes get a number or an author order wrong, and that kind of mistake reads as perfectly plausible. Check every draft against the original before publishing it.',
  },

  // -- Editor / 编辑器 -----------------------------------------------------
  'editor.untitled': { zh: '未命名', en: 'Untitled' },
  'editor.fixRequired': { zh: '请先填写标红的必填项', en: 'Fill in the highlighted required fields first' },
  'editor.template': { zh: '选择模板', en: 'Choose a template' },
  'editor.templateLede': {
    zh: '模板决定内容的骨架，保证不同人写出来的页面结构一致。之后可以自由增删。',
    en: 'A template sets the skeleton so pages by different authors stay consistent. You can add or remove blocks afterwards.',
  },
  'editor.useTemplate': { zh: '使用此模板', en: 'Use this template' },
  'editor.blocks': { zh: '内容块', en: 'Blocks' },
  'editor.addBlock': { zh: '插入内容块', en: 'Add block' },
  'editor.blockEmpty': { zh: '还没有内容块，从上方选一个模板或直接插入。', en: 'No blocks yet — pick a template above or insert one directly.' },
  'editor.addBlockHere': { zh: '在这里插入内容块', en: 'Insert a block here' },
  'editor.blockIndex': { zh: '第 {n} 块', en: 'Block {n}' },
  'editor.dragHandle': { zh: '拖动调整顺序', en: 'Drag to reorder' },
  'editor.removeBlock': { zh: '删除这个块', en: 'Remove block' },
  'editor.duplicateBlock': { zh: '复制这个块', en: 'Duplicate block' },
  'editor.inlineMarks': {
    zh: '支持 **粗体**、*斜体*、`代码`、[链接](https://…)',
    en: 'Supports **bold**, *italic*, `code`, [link](https://…)',
  },
  'editor.preview': { zh: '预览', en: 'Preview' },
  'editor.previewTitle': { zh: '预览（与公开页一致）', en: 'Preview (exactly as published)' },
  'editor.exitPreview': { zh: '退出预览', en: 'Exit preview' },
  'editor.saveDraft': { zh: '存草稿', en: 'Save draft' },
  'editor.metadata': { zh: '基本信息', en: 'Details' },
  'editor.autosaved': { zh: '{time} 自动保存', en: 'Autosaved {time}' },
  'editor.autosaveFailed': { zh: '自动保存失败，请手动保存', en: 'Autosave failed — save manually' },
  'editor.autosavePaused': {
    zh: '已发布内容不自动保存，请手动保存',
    en: 'Published records are not autosaved — save manually',
  },
  'editor.dropImage': { zh: '拖入图片，或点击选择', en: 'Drop an image, or click to choose' },
  'editor.dropVideo': { zh: '拖入视频，或点击选择', en: 'Drop a video, or click to choose' },
  'editor.dropAudio': { zh: '拖入音频，或点击选择', en: 'Drop an audio file, or click to choose' },
  'editor.dropDoc': { zh: '拖入 PDF 或文档，或点击选择', en: 'Drop a PDF or document, or click to choose' },
  'editor.dropCertificate': {
    zh: '拖入证书 PDF 或图片，站点统一显示为 PNG',
    en: 'Drop a certificate PDF or image — the site always displays PNG',
  },
  'editor.certificateOriginal': { zh: '原始文件', en: 'Original file' },
  // Percent of an in-flight upload. Identical in both languages, but it still
  // goes through t() so no component has to embed a literal.
  // 上传进度百分比。两种语言写法相同，但依然走 t()，免得组件里嵌字面量。
  'editor.uploadPercent': { zh: '{n}%', en: '{n}%' },
  'editor.convertingPdf': { zh: '正在把 PDF 转成 PNG…', en: 'Converting the PDF to PNG…' },
  'editor.pdfConverted': { zh: 'PDF 已转为 PNG', en: 'PDF converted to PNG' },
  'editor.pdfConvertFailed': {
    zh: 'PDF 转换失败，已保留原文件，请手动上传一张截图',
    en: 'PDF conversion failed. The original is kept — please upload a screenshot instead.',
  },

  // -- Errors / 错误 -------------------------------------------------------
  'error.generic': { zh: '出错了', en: 'Something went wrong' },
  'error.network': { zh: '无法连接服务器', en: 'Cannot reach the server' },
  'error.networkBody': {
    zh: '后端服务可能没有启动。在项目目录执行 npm run dev 后重试。',
    en: 'The backend may not be running. Start it with `npm run dev` and try again.',
  },
  'error.notFound': { zh: '页面不存在', en: 'Page not found' },
  'error.notFoundBody': { zh: '链接可能已经失效。', en: 'The link may be out of date.' },
  'error.forbidden': { zh: '没有权限', en: 'Not permitted' },
  'error.required': { zh: '这一项必填', en: 'This field is required' },
  'error.uploadTooLarge': { zh: '文件超过大小限制', en: 'File exceeds the size limit' },
  'error.uploadWrongType': { zh: '不支持这种文件类型', en: 'Unsupported file type' },

  // -- Footer / 页脚 -------------------------------------------------------
  'footer.copyright': { zh: '© {year} {name}', en: '© {year} {name}' },
  'footer.builtNote': { zh: '内容由课题组维护', en: 'Maintained by the group' },
  'footer.visits': { zh: '累计访问 {n}', en: '{n} visits' },

  // -- Statistics / 访问统计 -------------------------------------------------
  'stats.title': { zh: '访问统计', en: 'Visitors' },
  'stats.lede': {
    zh: '同一访客每天计一次。只保存汇总数字，不保存 IP 地址。',
    en: 'One visit per visitor per day. Only totals are stored — never an IP address.',
  },
  'stats.today': { zh: '今日访问', en: 'Today' },
  'stats.total': { zh: '总访问', en: 'Total' },
  'stats.thisYear': { zh: '本年访问', en: 'This year' },
  'stats.countries': { zh: '来访国家 / 地区', en: 'Countries' },
  'stats.byYear': { zh: '按年统计', en: 'By year' },
  'stats.byCountry': { zh: '访客来源', en: 'Where visitors are' },
  'stats.recentDays': { zh: '最近 30 天', en: 'Last 30 days' },
  'stats.since': { zh: '统计自 {date}', en: 'Since {date}' },
  'stats.unknownCountry': { zh: '未能识别', en: 'Unresolved' },
  'stats.empty': { zh: '还没有访问记录', en: 'No visits recorded yet' },
  'stats.emptyBody': {
    zh: '统计从本功能上线之日开始累计。打开一次公开页面即可看到第一条。',
    en: 'Counting starts from the day this was switched on. Open a public page once to see the first entry.',
  },
  'stats.mapNote': {
    zh: '颜色深浅表示访问量。灰色为无访问或未能识别归属地。',
    en: 'Shading is visit volume. Grey means no visits, or a location that could not be resolved.',
  },
  'footer.links': { zh: '快速链接', en: 'Links' },
  'footer.contact': { zh: '联系方式', en: 'Contact' },
}
